import {
  Modal,
  Button,
  Badge,
  Notice,
  Icon,
} from "@conuti-das/prince-ui";
import { Flex, Spinner, princeIcon } from "@/ui-prince";
import type { ReviewResult, ReviewFinding } from "@/api/mapperApi";

const MONO = "var(--sapFontMonoFamily, monospace)";
const LABEL = "var(--sapContent_LabelColor)";
const BORDER = "var(--sapList_BorderColor)";

const SEVERITY_TONE: Record<ReviewFinding["severity"], "info" | "critical" | "negative"> = {
  info: "info",
  warn: "critical",
  error: "negative",
};

function ediLabel(a?: Record<string, unknown>): string {
  if (!a) return "—";
  const seg = (a.segment as string) ?? "?";
  const qual = a.qualifier ? `+${a.qualifier as string}` : "";
  const sg = a.segment_group ? `${a.segment_group as string} / ` : "";
  const de = a.data_element ? ` · DE${a.data_element as string}` : "";
  return `${sg}${seg}${qual}${de}`;
}

function bo4eLabel(b?: { bo: string; path: string }): string {
  if (!b) return "—";
  return `${b.bo}.${b.path}`;
}

function transformLabel(t?: Record<string, unknown>): string | null {
  if (!t) return null;
  const type = (t.type as string) ?? "?";
  return type;
}

function FindingRow({
  finding,
  onAccept,
  onOpenEntry,
}: {
  finding: ReviewFinding;
  onAccept: (f: ReviewFinding) => void;
  onOpenEntry: (f: ReviewFinding) => void;
}): JSX.Element {
  const transform = transformLabel(finding.suggested_transform);
  return (
    <div
      data-testid="review-finding"
      data-kind={finding.kind}
      style={{
        border: `1px solid ${BORDER}`,
        borderRadius: "4px",
        padding: "8px 10px",
        marginBottom: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Flex style={{ alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <Badge tone={SEVERITY_TONE[finding.severity] === "info" ? "blue" : SEVERITY_TONE[finding.severity] === "critical" ? "orange" : "red"}>
          {finding.kind}
        </Badge>
        {finding.kind === "missing" && (
          <Button
            variant="filled"
            data-testid="accept-finding"
            onPress={() => onAccept(finding)}
          >
            Übernehmen
          </Button>
        )}
        {finding.kind === "mismatch" && (
          <Button
            variant="plain"
            data-testid="open-entry"
            onPress={() => onOpenEntry(finding)}
          >
            Im Editor öffnen
          </Button>
        )}
      </Flex>

      <div style={{ fontFamily: MONO, fontSize: "0.8rem" }}>
        {ediLabel(finding.edi_anchor)}
        <span style={{ color: LABEL }}> → </span>
        {bo4eLabel(finding.bo4e_anchor)}
      </div>

      {transform && (
        <div style={{ fontSize: "0.74rem", color: LABEL }}>
          Transform: <span style={{ fontFamily: MONO }}>{transform}</span>
          {finding.suggested_direction ? ` · Richtung: ${finding.suggested_direction}` : ""}
        </div>
      )}

      <span style={{ fontSize: "0.8rem" }}>{finding.note}</span>
    </div>
  );
}

export function ReviewPanel(props: {
  open: boolean;
  result: ReviewResult | null;
  loading?: boolean;
  error?: string | null;
  onClose: () => void;
  onAccept: (finding: ReviewFinding) => void;
  onAcceptAll: () => void;
  onOpenEntry: (finding: ReviewFinding) => void;
}): JSX.Element {
  const { open, result, loading, error, onClose, onAccept, onAcceptAll, onOpenEntry } = props;

  const hasMissing = !!result?.findings.some((f) => f.kind === "missing");

  return (
    <Modal
      isOpen={open}
      onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}
      title="KI-Review"
      className="prn-dialog-wide"
      data-testid="review-panel"
    >
      <Flex justifyContent="End" style={{ gap: "8px", marginBottom: "var(--s-3)" }}>
        {!loading && !error && hasMissing && (
          <Button
            variant="filled"
            data-testid="accept-all"
            onPress={onAcceptAll}
          >
            Alle Vorschläge übernehmen
          </Button>
        )}
        <Button variant="plain" onPress={onClose}>
          <Icon name={princeIcon("decline")} size={16} />
          Schließen
        </Button>
      </Flex>

      {open && (
        <div style={{ padding: "8px 4px", minWidth: "32rem" }}>
          {loading ? (
            <div
              style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "2rem", gap: "8px" }}
              data-testid="review-loading"
            >
              <Spinner size="m" />
              <span>KI prüft das Mapping …</span>
            </div>
          ) : error ? (
            <div data-testid="review-error">
              <Notice tone="negative">
                {error}
              </Notice>
            </div>
          ) : result ? (
            <>
              <span style={{ fontSize: "0.9rem", fontWeight: 600 }}>{result.summary}</span>

              <div
                data-testid="review-stats"
                style={{ fontSize: "0.78rem", color: LABEL, margin: "6px 0" }}
              >
                {result.stats.items} Items · {result.stats.batches} Batches ·{" "}
                {result.stats.missing} fehlend · {result.stats.mismatch} unstimmig
              </div>

              {result.truncated && (
                <div data-testid="review-truncated" style={{ marginBottom: "8px" }}>
                  <Notice tone="critical">
                    Nicht alle Items wurden geprüft (Obergrenze erreicht). Das Ergebnis ist
                    unvollständig.
                  </Notice>
                </div>
              )}

              <div data-testid="review-findings">
                {result.findings.map((f, i) => (
                  <FindingRow
                    key={i}
                    finding={f}
                    onAccept={onAccept}
                    onOpenEntry={onOpenEntry}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
